import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Loader2, Plus, Trash2, Info } from 'lucide-react';
import api from '../../common/apiClient';
import FormOffCanvas from '../ui/FormOffCanvas';
import SelectDropdown from '../../customStyles/SelectDropdown';
import SummaryApi from '../../common/SummaryApi';

const employmentTypeOptions = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'intern', label: 'Intern' },
  { value: 'consultant', label: 'Consultant' }
];

// Standard default salary breakup components for automatic pre-population
const defaultSalaryBreakup = [
  { component: 'Basic Salary', amount: 0, frequency: 'annual' },
  { component: 'House Rent Allowance (HRA)', amount: 0, frequency: 'annual' },
  { component: 'Special Allowance', amount: 0, frequency: 'annual' }
];

const OfferForm = ({ isOpen, onClose, mode = 'create', offer, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  
  const [formData, setFormData] = useState({
    candidateId: '',
    departmentId: '',
    designationId: '',
    employmentType: 'full_time',
    joiningDate: '',
    expiryDate: '', // Added Expiry Date parameter
    ctc: 0, // Auto-calculated based on component amounts
    workLocation: '',
    probationPeriodMonths: 3,
    salaryBreakup: []
  });

  // Fetch departments and designations dropdown lists
  const fetchDropdownConfigs = async () => {
    try {
      const deptRes = await api.get(SummaryApi.getAllDepartments.url);
      if (deptRes.data.success) {
        setDepartments(deptRes.data.data || []);
      }
      const desigRes = await api.get(SummaryApi.getAllDesignations.url);
      if (desigRes.data.success) {
        setDesignations(desigRes.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load dependency configurations:', err);
    }
  };

  // Fetch all selected or offered candidates only (ATS Logic Rule)
  const fetchSelectedCandidates = async () => {
    try {
      const response = await api.get(SummaryApi.getAllCandidates.url);
      if (response.data.success) {
        // Filter candidates who are in 'selected' or 'offered' stage
        const filtered = (response.data.data || []).filter(c => 
          ['selected', 'offered'].includes(c.currentStage)
        );
        setCandidates(filtered.map(c => ({
          _id: c._id,
          fullName: `${c.firstName} ${c.lastName} (${c.email})`
        })));
      }
    } catch (err) {
      console.error('Failed to load selected candidate list:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDropdownConfigs();
      fetchSelectedCandidates();
    }
  }, [isOpen]);

  // Load selected Offer parameters in Edit mode
  useEffect(() => {
    if (mode === 'edit' && offer) {
      setFormData({
        candidateId: offer.candidateId?._id || offer.candidateId || '',
        departmentId: offer.departmentId?._id || offer.departmentId || '',
        designationId: offer.designationId?._id || offer.designationId || '',
        employmentType: offer.employmentType || 'full_time',
        joiningDate: offer.joiningDate ? new Date(offer.joiningDate).toISOString().split('T')[0] : '',
        expiryDate: offer.expiryDate ? new Date(offer.expiryDate).toISOString().split('T')[0] : '',
        ctc: offer.ctc || 0,
        workLocation: offer.workLocation || '',
        probationPeriodMonths: offer.probationPeriodMonths || 3,
        salaryBreakup: offer.salaryBreakup || []
      });
    } else if (mode === 'create') {
      setFormData({
        candidateId: '',
        departmentId: '',
        designationId: '',
        employmentType: 'full_time',
        joiningDate: '',
        expiryDate: '',
        ctc: 0,
        workLocation: '',
        probationPeriodMonths: 3,
        salaryBreakup: JSON.parse(JSON.stringify(defaultSalaryBreakup)) // Pre-populate default components safely
      });
    }
  }, [mode, offer, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = value;

    // 👈 Constraint: Prevent negative values for probation period months
    if (name === 'probationPeriodMonths') {
      const numVal = Number(value);
      parsedValue = numVal < 0 ? 0 : numVal;
    }

    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  // Helper utility to dynamically calculate CTC from all component amounts
  const calculateTotalCtc = (breakupArray) => {
    return breakupArray.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  };

  // Salary Breakup dynamic array helpers with automatic CTC updating
  const handleAddBreakupComponent = () => {
    setFormData(prev => ({
      ...prev,
      salaryBreakup: [...prev.salaryBreakup, { component: '', amount: 0, frequency: 'annual' }]
    }));
  };

  const handleRemoveBreakupComponent = (idx) => {
    const arr = [...formData.salaryBreakup];
    arr.splice(idx, 1);
    const updatedCtc = calculateTotalCtc(arr);
    setFormData(prev => ({ 
      ...prev, 
      salaryBreakup: arr,
      ctc: updatedCtc
    }));
  };

  const handleBreakupChange = (idx, field, val) => {
    const arr = [...formData.salaryBreakup];
    
    // 👈 Constraint: Prevent negative values for individual salary component amounts
    if (field === 'amount') {
      const numVal = Number(val);
      arr[idx][field] = numVal < 0 ? 0 : numVal;
    } else {
      arr[idx][field] = val;
    }

    const updatedCtc = calculateTotalCtc(arr);
    setFormData(prev => ({ 
      ...prev, 
      salaryBreakup: arr,
      ctc: updatedCtc
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.candidateId || !formData.departmentId || !formData.joiningDate || !formData.expiryDate) {
      toast.warning('Please specify Candidate, Department, Joining Date and Expiry Date');
      return;
    }
    if (formData.ctc <= 0) {
      toast.warning('Total CTC cannot be zero. Please configure component breakdown amounts.');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (mode === 'create') {
        response = await api.post(SummaryApi.createCandidateOffer.url, formData);
      } else {
        response = await api.put(SummaryApi.updateCandidateOffer.url(offer._id), formData);
      }

      if (response.data.success) {
        toast.success(mode === 'create' ? 'Offer Package created successfully' : 'Offer Package updated successfully');
        if (onSave) onSave();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failure');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormOffCanvas 
      isOpen={isOpen} 
      onClose={onClose} 
      title={mode === 'create' ? 'Create Candidate Offer Package' : 'Edit Offer Details'}
    >
      <form onSubmit={handleSubmit} className="space-y-4 mb-12 md:mb-16">
        
        {/* Candidate Selector (Disabled in EDIT mode, custom descending z-indexes for overlapping prevention) */}
        <div className={mode === 'edit' ? 'opacity-50 pointer-events-none' : ''}>
          <SelectDropdown 
            label="Selected Candidate *"
            options={candidates}
            value={formData.candidateId}
            onChange={(val) => setFormData(prev => ({ ...prev, candidateId: val }))}
            valueKey="_id"
            labelKey="fullName"
            searchable={true}
            zIndex={75}
            multiple={false}
          />
        </div>

        {/* Department Selection */}
        <SelectDropdown 
          label="Department *"
          options={departments}
          value={formData.departmentId}
          onChange={(val) => setFormData(prev => ({ ...prev, departmentId: val }))}
          valueKey="_id"
          labelKey="name"
          searchable={true}
          zIndex={66}
          multiple={false}
        />

        {/* Designation Selection */}
        <SelectDropdown 
          label="Designation (Optional)"
          options={designations}
          value={formData.designationId}
          onChange={(val) => setFormData(prev => ({ ...prev, designationId: val }))}
          valueKey="_id"
          labelKey="title"
          searchable={true}
          zIndex={66}
          multiple={false}
        />

        {/* Employment Type Selection */}
        <SelectDropdown 
          label="Employment Type"
          options={employmentTypeOptions}
          value={formData.employmentType}
          onChange={(val) => setFormData(prev => ({ ...prev, employmentType: val }))}
          valueKey="value"
          labelKey="label"
          searchable={false}
          zIndex={66}
          multiple={false}
        />

        {/* Date Inputs Panel */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Target Joining Date *</label>
            <input 
              type="date" 
              name="joiningDate" 
              required 
              value={formData.joiningDate} 
              onChange={handleChange} 
              className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Offer Expiry Date *</label>
            <input 
              type="date" 
              name="expiryDate" 
              required 
              value={formData.expiryDate} 
              onChange={handleChange} 
              className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Work Location</label>
            <input 
              type="text" 
              name="workLocation" 
              value={formData.workLocation} 
              onChange={handleChange} 
              placeholder="e.g. Chennai Office"
              className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Probation Period (Months)</label>
            <input 
              type="number" 
              name="probationPeriodMonths" 
              min="0"
              value={formData.probationPeriodMonths} 
              onChange={handleChange} 
              onWheel={(e) => e.target.blur()} // 👈 UX Fix: Prevent value changes on mouse scroll wheel
              className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
            />
          </div>
        </div>

        {/* Highlighted Auto-calculated Total Annual CTC indicator */}
        <div className="bg-red-50/30 border border-red-100 p-3 rounded-lg space-y-1">
          <span className="text-[10px] font-bold text-primary uppercase block">Total Annual CTC (Calculated Automatically)</span>
          <p className="text-sm font-extrabold text-gray-800">
            INR {formData.ctc.toLocaleString('en-IN')} / year
          </p>
        </div>

        {/* Dynamic Salary Breakup Section */}
        <div className="border-t pt-4 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Salary Component Breakup</h4>
            <button 
              type="button" 
              onClick={handleAddBreakupComponent}
              className="text-[10px] text-primary font-bold hover:underline flex items-center gap-0.5"
            >
              <Plus className="h-3 w-3" /> Add Component
            </button>
          </div>

          {formData.salaryBreakup.length > 0 ? (
            <div className="space-y-3">
              {formData.salaryBreakup.map((comp, idx) => (
                <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2.5 rounded-lg border">
                  <input 
                    type="text" 
                    required 
                    placeholder="e.g. Basic Salary"
                    value={comp.component}
                    onChange={(e) => handleBreakupChange(idx, 'component', e.target.value)}
                    className="flex-1 border rounded p-1.5 text-xs outline-none bg-white"
                  />
                  <input 
                    type="number" 
                    required 
                    min="0"
                    placeholder="Amount (Annual)"
                    value={comp.amount || ''}
                    onChange={(e) => handleBreakupChange(idx, 'amount', e.target.value)}
                    onWheel={(e) => e.target.blur()} // 👈 UX Fix: Prevent value changes on mouse scroll wheel
                    className="w-14 md:w-28 border rounded p-1.5 text-xs outline-none bg-white"
                  />
                  <button 
                    type="button" 
                    onClick={() => handleRemoveBreakupComponent(idx)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex gap-1.5 items-center bg-gray-50 p-2.5 rounded-lg border text-[10px] text-gray-400 italic">
              <Info className="h-4 w-4 text-gray-400" />
              <span>No salary components added. Compensation structures require at least one component to process.</span>
            </div>
          )}
        </div>

        {/* Action Submit Button */}
        <button 
          type="submit" 
          disabled={saving} 
          className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-1.5 shadow-sm"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? 'Processing Offer Package...' : mode === 'create' ? 'Create Offer Package' : 'Save Offer Changes'}
        </button>

      </form>
    </FormOffCanvas>
  );
};

export default OfferForm;